// src/ROBPage.js

import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const ROBPage = () => {
  const [templateName, setTemplateName] = useState('');
  const [fields, setFields] = useState([
    {
      section: '',       // <-- added
      fieldName: '',
      type: 'String',
      dropdownList: '',
      validationRule: '',
      mandatory: false,
    },
  ]);
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/rob`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      } else {
        console.error('Failed to fetch ROB templates');
      }
    } catch (error) {
      console.error('Error fetching ROB templates:', error);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/rob/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setTemplates(tpls => tpls.filter(t => t._id !== id));
      } else {
        console.error('Failed to delete ROB template');
      }
    } catch (error) {
      console.error('Error deleting ROB template:', error);
    }
  };

  const addField = () => {
    setFields(flds => [
      ...flds,
      { section: '', fieldName: '', type: 'String', dropdownList: '', validationRule: '', mandatory: false },
    ]);
  };

  const removeField = (idx) => {
    setFields(flds => flds.filter((_, i) => i !== idx));
  };

  const handleFieldChange = (idx, key, value) => {
    setFields(flds => {
      const copy = [...flds];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { templateName, fields };
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/rob`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMessage('ROB template saved successfully!');
        setTemplateName('');
        setFields([{ section: '', fieldName: '', type: 'String', dropdownList: '', validationRule: '', mandatory: false }]);
        fetchTemplates();
      } else {
        setMessage('Failed to save ROB template');
      }
    } catch (error) {
      console.error('Error saving ROB template:', error);
      setMessage('An error occurred while saving the template.');
    }
  };

  return (
    <div className="container my-4">
      <h2>ROB Template</h2>
      <p>Configure the fields for the ROB section of the Performance Report.</p>

      {/* Existing Templates */}
      <h4>Existing Templates</h4>
      {templates.length > 0 ? (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>#</th>
              <th>Template Name</th>
              <th style={{ width: '10%' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl, i) => (
              <tr key={tpl._id}>
                <td>{i + 1}</td>
                <td>{tpl.templateName}</td>
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteTemplate(tpl._id)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No ROB templates found.</p>
      )}

      {/* Create / Edit Form */}
      <h4 className="mt-4">Create New Template</h4>
      <form onSubmit={handleSubmit}>
        {/* Template Name */}
        <div className="mb-3">
          <label htmlFor="robTemplateName" className="form-label">Template Name</label>
          <input
            id="robTemplateName"
            type="text"
            className="form-control"
            placeholder="Enter template name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            required
          />
        </div>

        {/* Fields */}
        <h5>Fields</h5>
        {fields.map((field, idx) => (
          <div key={idx} className="mb-3 border p-3">
            <div className="row g-3 align-items-center">
              {/* Section */}
              <div className="col-md-2">
                <label className="form-label">Section</label>
                <input
                  type="text"
                  className="form-control"
                  value={field.section}
                  onChange={e => handleFieldChange(idx, 'section', e.target.value)}
                  placeholder="Enter section"
                  required
                />
              </div>

              {/* Field Name */}
              <div className="col-md-3">
                <label className="form-label">Field Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={field.fieldName}
                  onChange={e => handleFieldChange(idx, 'fieldName', e.target.value)}
                  placeholder="Enter field name"
                  required
                />
              </div>

              {/* Type of Entry */}
              <div className="col-md-2">
                <label className="form-label">Type of Entry</label>
                <select
                  className="form-select"
                  value={field.type}
                  onChange={e => handleFieldChange(idx, 'type', e.target.value)}
                >
                  <option value="String">String</option>
                  <option value="Datetime">Datetime</option>
                  <option value="Number">Number</option>
                  <option value="Decimal">Decimal</option>
                  <option value="Dropdown">Dropdown</option>
                </select>
              </div>

              {/* Validation Rule */}
              <div className="col-md-3">
                <label className="form-label">Validation Rule</label>
                <input
                  type="text"
                  className="form-control"
                  value={field.validationRule}
                  onChange={e => handleFieldChange(idx, 'validationRule', e.target.value)}
                  placeholder="Enter validation rule"
                />
              </div>

              {/* Mandatory */}
              <div className="col-md-1 text-center">
                <label className="form-label">Mandatory</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={field.mandatory}
                    onChange={e => handleFieldChange(idx, 'mandatory', e.target.checked)}
                  />
                </div>
              </div>

              {/* Remove Field */}
              <div className="col-md-1 text-end">
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => removeField(idx)}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Dropdown Options */}
            {field.type === 'Dropdown' && (
              <div className="row mt-3">
                <div className="col-12">
                  <label className="form-label">Dropdown List (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={field.dropdownList}
                    onChange={e => handleFieldChange(idx, 'dropdownList', e.target.value)}
                    placeholder="Enter options separated by commas"
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Actions */}
        <div className="d-flex gap-2 mb-3">
          <button type="button" className="btn btn-secondary" onClick={addField}>
            Add New Field
          </button>
          <button type="submit" className="btn btn-primary">
            Save Template
          </button>
        </div>
      </form>

      {message && <div className="alert alert-info mt-3">{message}</div>}
    </div>
  );
};

export default ROBPage;
